// numberToFrenchWords.js

/**
 * Converts a number to its French words representation
 * Handles whole numbers and decimal parts (centimes)
 * 
 * @param {number} number - The number to convert
 * @returns {string} - The number written out in French words
 */
export const numberToFrenchWords = (number) => {
    const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
    const scales = ['', 'mille', 'million', 'milliard', 'billion'];
    
    const convertChunk = (num) => {
      if (num === 0) return '';
      
      let words = '';
      const hundredsDigit = Math.floor(num / 100);
      let remainder = num % 100;
      
      // Handle hundreds
      if (hundredsDigit > 0) {
        words += hundredsDigit === 1
          ? 'cent '
          : `${units[hundredsDigit]} cent${remainder === 0 ? 's ' : ' '}`;
      }
      
      // Handle tens and units
      if (remainder >= 20) {
        const tensIndex = Math.floor(remainder / 10);
        const unitsIndex = remainder % 10;
        
        if (tensIndex === 7 || tensIndex === 9) {
          words += tens[tensIndex];
          // Handle "et" for 71/91 specifically
          if (unitsIndex === 1) {
            words += tensIndex === 7 ? ' et ' : '-';
            words += units[10 + unitsIndex];
          } else {
            words += unitsIndex > 0 ? `-${units[10 + unitsIndex]}` : '';
          }
          words += ' ';
        } else {
          words += tens[tensIndex];
          // Handle "et" for 21/31/41/etc.
          if (unitsIndex === 1 && tensIndex !== 8) {
            words += ' et ';
          } else if (unitsIndex > 0) {
            words += '-';
          }
          words += units[unitsIndex] + ' ';
        }
        
        // Add "s" to quatre-vingt ONLY when it's exactly 80 (no remainder)
        if (tensIndex === 8 && unitsIndex === 0) {
          words = words.replace(/quatre-vingt$/, 'quatre-vingts');
        }
      } else if (remainder > 0) {
        words += units[remainder] + ' ';
      }
      
      return words.trim() + ' ';
    };
    
    const convert = (num) => {
      if (num === 0) return 'zéro';
      
      let words = '';
      let scaleIndex = 0;
      
      while (num > 0) {
        const chunk = num % 1000;
        if (chunk > 0) {
          let chunkWords = convertChunk(chunk);
          let scaleWord = scales[scaleIndex];
          
          // Handle pluralization for millions/milliards
          if (scaleIndex >= 2 && chunk > 1) {
            scaleWord += 's';
          }
          
          // Special case for "mille"
          if (scaleIndex === 1 && chunk === 1) {
            chunkWords = '';
          }
          
          words = chunkWords + scaleWord + ' ' + words;
        }
        num = Math.floor(num / 1000);
        scaleIndex++;
      }
      
      return words.replace(/\s+/g, ' ').trim();
    };
    
    // Handle decimal part
    const wholePart = Math.floor(number);
    const decimalPart = Math.round((number - wholePart) * 100);
    
    let result = convert(wholePart);
    
    if (decimalPart > 0) {
      const centimes = decimalPart === 1 ? 'un centime' : `${convert(decimalPart)} centimes`;
      result += ` DH et ${centimes}`;
    }
    
    return result;
  };
  
  export default numberToFrenchWords;